import { memo } from 'react';
import { Link } from 'react-router-dom';
import {
  Grid, Typography, Fab, Stack,
  Container,
} from '@mui/material';
import { Configure, InstantSearch } from 'react-instantsearch-hooks-web';
import { useIsMobile } from '@client/hooks/dom.hooks';
import { algoliaClient } from '@client/algolia';
import { getAlgoliaIndexName, serializeFilters } from '@sda/utils/algolia';
import { AlgoliaIndices } from '@sda/enums/algolia';
import { Hits } from '@client/algolia/InstantSearch/Hits';
import { BeaconStatuses, ProfileTypes } from '@sda/enums';
import { getEventLocation } from '@sda/utils/string';
import { getDateRange } from '@sda/utils/date';
import { showCallAttributesToRetrieve } from '../Connect/Main/FetchShowCalls/constants';
import { ConnectCard } from '../Connect/common/ConnectCard';
import type { AlgoliaBeacon } from '@sda/helpers/algolia.helpers';
import type { AlgoliaFilter } from '@sda/utils/algolia/interfaces';

const defaultFilters = {
  'publicBeacon.endDate._seconds': {
    type: 'futureDate',
  },
  'publicBeacon.beaconStatus': {
    type: 'string',
    value: BeaconStatuses.ACTIVE as string,
  },
  hasPublicImageUrl: {
    type: 'tag',
  },
  'publicBeacon.isPublicCall': {
    type: 'boolean',
    value: true,
  },
  'publicBeacon.profileType': {
    type: 'string',
    value: ProfileTypes.VENUE as string,
  },
};

export const Hosts = memo(() => {
  const isMobile = useIsMobile();

  return (
    <Container maxWidth="md">
      <Stack spacing={3}>
        <div>
          <Grid
            container
            spacing={isMobile ? 0 : 3}
            alignItems="center"
            justifyContent={isMobile ? 'space-between' : 'flex-start'}
          >
            <Grid item>
              <Typography variant={isMobile ? 'h6' : 'h4'}>
                Discover Hosts
              </Typography>
            </Grid>

            <Grid item>
              <Fab
                color="info"
                component={Link}
                to="/connect/hosts"
              >
                View More
              </Fab>
            </Grid>
          </Grid>
        </div>

        <InstantSearch
          searchClient={algoliaClient}
          indexName={getAlgoliaIndexName(AlgoliaIndices.BEACONS)}
        >
          <Configure
            filters={serializeFilters(defaultFilters as Record<string, AlgoliaFilter>)}
            hitsPerPage={3}
            attributesToRetrieve={showCallAttributesToRetrieve}
            attributesToHighlight={[]}
            aroundLatLngViaIP
            aroundPrecision={100000}
          />

          <div>
            <Hits<any, AlgoliaBeacon>
              component={Grid}
              container
              spacing={3}
              hitComponent={({ hit }) => (
                <Grid
                  item
                  xs={12}
                  sm={6}
                  md={4}
                >
                  <ConnectCard
                    imgSrc={hit.publicBeacon.profileData.publicImageUrl}
                    path={`/connect/${hit.objectID}`}
                    title={hit.publicBeacon.profileData.name}
                    location={getEventLocation(hit.publicBeacon)}
                    numReviews={hit.publicBeacon.profileData.numReviews}
                    score={hit.publicBeacon.profileData.score}
                    dateRange={getDateRange(hit.publicBeacon)}
                    isFeatured={hit.publicBeacon.isFeatured}
                    badge={hit.publicBeacon.profileData.badge}
                  />
                </Grid>
              )}
            />
          </div>
        </InstantSearch>
      </Stack>
    </Container>
  );
});
